import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ICartEntry } from 'features/cart/types';
import { usePriceOrderV2Input } from 'features/order/hooks/use-price-order-v2-input';

import {
  CartServiceMode,
  DeliveryStatus,
  IMenuSelectionInput,
  usePriceOrderV2Mutation,
} from 'generated/graphql-gateway';
import { useOrderStatus } from 'hooks/order-status';
import { actions, useAppDispatch } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { OrderStatus } from 'state/order/types';
import { useServiceModeContext } from 'state/service-mode';
import { buildPriceDeliveryInput } from 'utils/cart';

const deriveMenuSelections = (
  menuSelections: readonly IMenuSelectionInput[] = []
): IMenuSelectionInput[] => {
  return menuSelections.map(selection => ({
    ...selection,
    menuSelections: deriveMenuSelections([...(selection.menuSelections ?? [])]),
  }));
};

type UseCartContinuePricingParams = {
  cartEntries: ICartEntry[];
  onSuccess: (rbiOrderId: string) => void;
};

export const useCartContinuePricing = ({
  cartEntries,
  onSuccess,
}: UseCartContinuePricingParams) => {
  const dispatch = useAppDispatch();
  const { deliveryAddress, deliveryInstructions, orderPhoneNumber, quoteId } = useOrderContext();
  const { serviceMode } = useServiceModeContext();
  const enablePaymentErrorContinue = useFlag(LaunchDarklyFlag.ENABLE_PAYMENT_ERROR_CONTINUE);
  const [pricingOrderId, setPricingOrderId] = useState<string | null>(null);
  const [pricingError, setPricingError] = useState(false);
  const [isPollingPending, setIsPollingPending] = useState(false);
  const isPricingAttemptActiveRef = useRef(false);

  const menuSelections = useMemo(
    () =>
      cartEntries.map(
        entry =>
          ({
            id: entry.id,
            quantity: entry.quantity,
            price: entry.price,
            menuSelections: deriveMenuSelections(entry.menuSelections ?? []),
          } as IMenuSelectionInput)
      ),
    [cartEntries]
  );

  const priceOrderInput = usePriceOrderV2Input(menuSelections, cartEntries);
  const priceOrderV2Input = useMemo(
    () => ({
      ...priceOrderInput,
      storeId: priceOrderInput.storeId ?? '',
      serviceMode: (priceOrderInput.serviceMode as unknown) as CartServiceMode,
    }),
    [priceOrderInput]
  );

  const [priceOrderV2Mutation, priceOrderV2Result] = usePriceOrderV2Mutation({
    context: { shouldNotBatch: true },
  });

  const successStatuses = enablePaymentErrorContinue
    ? [
        OrderStatus.PRICE_SUCCESSFUL,
        OrderStatus.INSERT_SUCCESSFUL,
        OrderStatus.PAYMENT_ERROR,
        OrderStatus.VALIDATION_ERROR,
      ]
    : [OrderStatus.PRICE_SUCCESSFUL, OrderStatus.VALIDATION_ERROR];

  const orderStatusQueryResult = useOrderStatus({
    failureStatuses: {
      delivery: [DeliveryStatus.QUOTE_ERROR, DeliveryStatus.QUOTE_UNAVAILABLE],
      pos: [OrderStatus.PRICE_ERROR],
    },
    onSuccess: serverOrder => {
      isPricingAttemptActiveRef.current = false;
      dispatch(actions.ordering.setPricedOrderId(serverOrder.rbiOrderId));
      dispatch(actions.ordering.setIsPricingOrder(false));
      onSuccess(serverOrder.rbiOrderId);
    },
    rbiOrderId: pricingOrderId,
    successStatuses: {
      delivery: DeliveryStatus.QUOTE_SUCCESSFUL,
      pos: successStatuses,
    },
    skip: !pricingOrderId,
  });

  const isPricingLoading =
    priceOrderV2Result.loading || orderStatusQueryResult.isLoading || isPollingPending;

  const resetPricing = useCallback(() => {
    isPricingAttemptActiveRef.current = false;
    setPricingOrderId(null);
    setPricingError(false);
    setIsPollingPending(false);
    priceOrderV2Result.reset?.();
    dispatch(actions.ordering.setIsPricingOrder(false));
  }, [dispatch, priceOrderV2Result]);

  const startPricing = useCallback(async () => {
    if (!menuSelections.length) {
      return;
    }

    isPricingAttemptActiveRef.current = true;

    if (!priceOrderV2Input.storeId || !priceOrderInput.serviceMode) {
      setPricingError(true);
      return;
    }

    setPricingOrderId(null);
    setPricingError(false);
    setIsPollingPending(false);
    dispatch(actions.ordering.setIsPricingOrder(true));

    try {
      const delivery = buildPriceDeliveryInput(
        {
          deliveryAddress,
          orderPhoneNumber,
          serviceMode: serviceMode!,
          deliveryInstructions,
          tipCents: 0,
          customerPhoneNumber: orderPhoneNumber,
        },
        quoteId
      );

      const { data } = await priceOrderV2Mutation({
        variables: {
          input: priceOrderV2Input,
          delivery: delivery ?? undefined,
        },
      });

      const rbiOrderId = data?.priceOrderV2?.rbiOrderId ?? null;
      if (!rbiOrderId) {
        setPricingError(true);
        dispatch(actions.ordering.setIsPricingOrder(false));
        return;
      }

      setIsPollingPending(true);
      setPricingOrderId(rbiOrderId);
    } catch {
      setPricingError(true);
      dispatch(actions.ordering.setIsPricingOrder(false));
    }
  }, [
    cartEntries.length,
    deliveryAddress,
    deliveryInstructions,
    dispatch,
    menuSelections,
    orderPhoneNumber,
    priceOrderInput,
    priceOrderV2Input,
    priceOrderV2Mutation,
    quoteId,
    serviceMode,
  ]);

  useEffect(() => {
    if (isPricingAttemptActiveRef.current && pricingOrderId && orderStatusQueryResult.failure) {
      setPricingError(true);
      dispatch(actions.ordering.setIsPricingOrder(false));
    }
  }, [dispatch, orderStatusQueryResult.failure, pricingOrderId]);

  useEffect(() => {
    if (
      orderStatusQueryResult.isLoading ||
      orderStatusQueryResult.success ||
      orderStatusQueryResult.failure ||
      pricingError
    ) {
      setIsPollingPending(false);
    }
  }, [
    orderStatusQueryResult.failure,
    orderStatusQueryResult.isLoading,
    orderStatusQueryResult.success,
    pricingError,
  ]);

  useEffect(() => {
    if (isPricingAttemptActiveRef.current && priceOrderV2Result.error && !pricingError) {
      setPricingError(true);
      dispatch(actions.ordering.setIsPricingOrder(false));
    }
  }, [dispatch, priceOrderV2Result.error, pricingError]);

  return {
    startPricing,
    resetPricing,
    isPricingLoading,
    pricingError,
  } as const;
};
